import React from 'react'
import intl from 'react-intl-universal'
import { connect, DispatchProp } from 'react-redux'
import {
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  FormFeedback,
  Label,
  Input,
  Button,
} from 'reactstrap'

import { getCustomEmbed, updateCustomEmbed } from '~/redux/settingsReducer'
import { EllipsisReduxStore } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./CustomEmbedSettingsModal.styles.scss')

interface Props {
  visible: boolean
  subtype?: string
  toggle: () => void
}

type ReduxProps = {
  customEmbed?: EllipsisReduxStore['settings']['customEmbed']['subtype']
} & DispatchProp

interface State {
  subtype: {
    value: string
    error: string
  }
  caption: {
    value: string
    error: string
  }
  view: {
    value: string
    error: string
  }
  edit: {
    value: string
    error: string
  }
  search: {
    value: string
    error: string
  }
  icon: {
    value: string
    error: string
  }
}

interface Action {
  type: 'FIELD_CHANGED' | 'FIELD_ERROR'
  payload: {
    name: string
    value: string
  }
}

const DEFAULT_LOAD_TIMEOUT = 10000

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'FIELD_CHANGED':
      return {
        ...state,
        [action.payload.name]: {
          error: '',
          value: action.payload.value,
        },
      }
    case 'FIELD_ERROR':
      return {
        ...state,
        [action.payload.name]: {
          ...state[action.payload.name],
          error: action.payload.value,
        },
      }
    default:
      throw new Error('Unrecognized action type')
  }
}

function handleBoltIcon(icon: string) {
  return icon !== 'bolt' ? icon : 'sun-o'
}

// Validating the scheme name, ://, and no spaces or double quotes
// is usually sufficient for URLs.
const urlPattern = '^(ftp|http|https)://[^ "]+$'

function CustomEmbedSettingsModal(props: Props & ReduxProps) {
  const mode = props.customEmbed ? 'update' : 'add'
  const icons = [
    'square',
    'wrench',
    'rss',
    'calendar',
    'sun-o',
    'music',
    'microphone',
    'play',
    'newspaper-o',
    'share-alt',
  ]

  const [state, dispatch] = React.useReducer(reducer, {
    subtype: {
      value: props.subtype || '',
      error: '',
    },
    caption: {
      value: props.customEmbed ? props.customEmbed.caption || '' : '',
      error: '',
    },
    view: {
      value: props.customEmbed ? props.customEmbed.view : '',
      error: '',
    },
    edit: {
      value: props.customEmbed ? props.customEmbed.edit : '',
      error: '',
    },
    search: {
      value: props.customEmbed ? props.customEmbed.search : '',
      error: '',
    },
    icon: {
      value: handleBoltIcon(props?.customEmbed?.icon ?? 'square'),
      error: '',
    },
  })

  const handleFieldChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget

    dispatch({
      type: 'FIELD_CHANGED',
      payload: {
        name,
        value,
      },
    })
  }

  const validateFields = (formElements: HTMLFormControlsCollection) => {
    let hasError = false

    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i]

      if (element instanceof HTMLInputElement) {
        if (element.validity.valueMissing) {
          dispatch({
            type: 'FIELD_ERROR',
            payload: {
              name: element.name,
              value: intl.get('CustomEmbedSettingsModal.errors.required'),
            },
          })
          hasError = true
        } else if (element.validity.patternMismatch) {
          dispatch({
            type: 'FIELD_ERROR',
            payload: {
              name: element.name,
              value: intl.get('CustomEmbedSettingsModal.errors.invalidUrl'),
            },
          })
          hasError = true
        }
      }
    }

    return hasError
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const hasError = validateFields(event.currentTarget.elements)

    if (!hasError) {
      props.dispatch(
        updateCustomEmbed({
          subtype: state.subtype.value,
          icon: state.icon.value,
          embed: {
            caption: state.caption.value,
            view: state.view.value,
            edit: state.edit.value,
            search: state.search.value,
            loadTimeout: DEFAULT_LOAD_TIMEOUT,
          },
        })
      )
      props.toggle()
    }
  }

  return (
    <Modal
      wrapClassName="bootstrap4"
      size="lg"
      className={styles.root}
      centered
      isOpen
      toggle={props.toggle}
    >
      <Form noValidate onSubmit={handleSubmit}>
        <ModalBody>
          <h4 className="title">
            {intl.get(`CustomEmbedSettingsModal.header.${mode}`)}
          </h4>
          <div className="group">
            <Label for="caption">
              {intl.get('CustomEmbedSettingsModal.fields.caption.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-name-input"
              name="caption"
              id="caption"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.caption.placeholder'
              )}
              value={state.caption.value}
              invalid={Boolean(state.caption.error)}
              onChange={handleFieldChange}
              readOnly={mode === 'update'}
              required
            />
            <FormFeedback>{state.caption.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="subtype">
              {intl.get('CustomEmbedSettingsModal.fields.subtype.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-display-type-input"
              name="subtype"
              id="subtype"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.subtype.placeholder'
              )}
              value={state.subtype.value}
              invalid={Boolean(state.subtype.error)}
              onChange={handleFieldChange}
              readOnly={mode === 'update'}
              required
            />
            <FormFeedback>{state.subtype.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="search">
              {intl.get('CustomEmbedSettingsModal.fields.search.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-search-input"
              name="search"
              id="search"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.search.placeholder'
              )}
              value={state.search.value}
              invalid={Boolean(state.search.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.search.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="view">
              {intl.get('CustomEmbedSettingsModal.fields.view.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-view-input"
              name="view"
              id="view"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.view.placeholder'
              )}
              value={state.view.value}
              invalid={Boolean(state.view.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.view.error}</FormFeedback>
          </div>
          <div className="group">
            <Label for="edit">
              {intl.get('CustomEmbedSettingsModal.fields.edit.label')}
            </Label>
            <Input
              data-testid="settings-custom-embed-edit-input"
              name="edit"
              id="edit"
              placeholder={intl.get(
                'CustomEmbedSettingsModal.fields.edit.placeholder'
              )}
              value={state.edit.value}
              invalid={Boolean(state.edit.error)}
              onChange={handleFieldChange}
              required
              pattern={urlPattern}
            />
            <FormFeedback>{state.edit.error}</FormFeedback>
          </div>
        </ModalBody>
        <ModalFooter className="footer">
          <div className="icon-group mr-auto">
            <Label>
              {intl.get('CustomEmbedSettingsModal.fields.icon.label')}
            </Label>
            <div className="button-container">
              {icons.map((icon, index) => {
                return (
                  <Button
                    key={index}
                    type="button"
                    name="icon"
                    data-testid={`icon-${icon}`}
                    value={icon}
                    color={icon === state.icon.value ? 'primary' : 'secondary'}
                    active={icon === state.icon.value}
                    outline
                    onClick={handleFieldChange}
                  >
                    <i className={`fa fa-${icon}`}></i>
                  </Button>
                )
              })}
            </div>
          </div>
          <Button
            data-testid="settings-custom-embed-cancel-btn"
            color="secondary"
            outline
            onClick={props.toggle}
          >
            {intl.get('CustomEmbedSettingsModal.buttons.cancel')}
          </Button>
          <Button
            data-testid="settings-custom-embed-add-btn"
            type="submit"
            color="primary"
          >
            {intl.get(`CustomEmbedSettingsModal.buttons.${mode}`)}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default connect((state: EllipsisReduxStore, props: Props) => ({
  customEmbed: getCustomEmbed(state)[props.subtype],
}))(CustomEmbedSettingsModal)
